import { createContext, useContext, useMemo, type ReactNode } from 'react';

import { useUser } from '~/modules/user/hook/use-user';
import {
    useBookMeetingGateEnabled,
    type BookMeetingGateIntent,
} from '~/modules-rocco/booking/book-meeting-gate-state';
import type { EnquiryModalContext } from '../hooks/use-enquiry-modal';
import {
    resolvePageCtas,
    type LoadedPageCtas,
    type PageCtaFallbackInputs,
    type PageCtas,
} from '../domain/page-ctas';

/** Everything a Book a meeting button knows about the page, before the button adds its own. */
export type PageBookIntentBase = Omit<
    BookMeetingGateIntent,
    | 'sessionIds'
    | 'formId'
    | 'formConfig'
    | 'detailsFirst'
    | 'pageType'
    | 'preferredTeamMemberId'
    | 'preferredTeamMemberName'
    | 'bookingFormId'
>;

export interface PageCtasContextValue {
    ctas: PageCtas;
    /** The enquiry modal's context for this page; each button adds its own source. */
    enquiry: EnquiryModalContext;
    /** The booking gate's intent for this page; each button adds its own person. */
    book: PageBookIntentBase;
}

const PageCtasContext = createContext<PageCtasContextValue | null>(null);

export interface PageCtasProviderProps {
    /** The loader's `enquiryCtasForPage` answer; null keeps the flat fields. */
    loaded: LoadedPageCtas | null;
    /** The flat fields, minus what the provider reads itself (visitor, gate flags). */
    fallback: Omit<PageCtaFallbackInputs, 'user' | 'bookGateEnabled'>;
    enquiry: EnquiryModalContext;
    book: PageBookIntentBase;
    children: ReactNode;
}

/**
 * Resolves the page's enquiry and Book a meeting buttons once, for every CTA
 * on the page to read through {@link useEnquireCta} and {@link useBookCta}.
 * Resolved on render rather than in the loader so the audience restriction
 * follows the visitor as they log in or out.
 */
export const PageCtasProvider = ({
    loaded,
    fallback,
    enquiry,
    book,
    children,
}: PageCtasProviderProps) => {
    const user = useUser();
    const bookGateEnabled = useBookMeetingGateEnabled();

    const ctas = useMemo(
        () => resolvePageCtas(loaded, { ...fallback, user, bookGateEnabled }),
        [loaded, fallback, user, bookGateEnabled],
    );
    const value = useMemo(
        () => ({ ctas, enquiry, book }),
        [ctas, enquiry, book],
    );

    return (
        <PageCtasContext.Provider value={value}>
            {children}
        </PageCtasContext.Provider>
    );
};

/** The page's resolved CTAs, or null outside a {@link PageCtasProvider}. */
export const usePageCtasContext = () => useContext(PageCtasContext);

/** The page's resolved CTAs; throws outside a {@link PageCtasProvider}. */
export const usePageCtas = (): PageCtasContextValue => {
    const value = useContext(PageCtasContext);
    if (!value) {
        throw new Error('usePageCtas must be used within a PageCtasProvider');
    }
    return value;
};
